import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/client';
import type { ProductSummary } from '../types/product';
import ProductGrid from '../components/ProductGrid';
import ProductDetail from '../components/ProductDetail';
import BulkEditBar from '../components/BulkEditBar';

const PAGE_SIZE = 20;

export default function Products() {
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCprcodes, setSelectedCprcodes] = useState<string[]>([]);
  const [detailCprcode, setDetailCprcode] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [lastKey, setLastKey] = useState<string | null>(null);
  const [pageHistory, setPageHistory] = useState<string[]>([]);
  const [filters, setFilters] = useState({
    category: '',
    brand: '',
    status: '',
  });
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const fetchPage = useCallback(async (paginationKey?: string | null) => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { page_size: PAGE_SIZE };
      if (filters.category) params.category = filters.category;
      if (filters.brand) params.brand = filters.brand;
      if (filters.status) params.status = filters.status;
      if (paginationKey) params.last_key = paginationKey;

      const resp = await api.listProducts(params);
      setProducts(resp.products);
      setLastKey(resp.last_key || null);
    } catch (err) {
      console.error('Failed to load products:', err);
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Keep a ref to fetchPage so the filter effect doesn't depend on it
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  // Reset pagination when filters change — watch filters directly via JSON.stringify
  const filtersJson = JSON.stringify(filters);
  useEffect(() => {
    setPageHistory([]);
    setLastKey(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // 300ms debounce for text inputs (category, brand); 0ms for status dropdown
    const delay = filters.category || filters.brand ? 300 : 0;
    debounceRef.current = setTimeout(() => fetchPageRef.current(), delay);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [filtersJson]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNextPage = () => {
    if (!lastKey) return;
    setPageHistory((prev) => [...prev, lastKey]);
    fetchPage(lastKey);
  };

  const handlePrevPage = () => {
    if (pageHistory.length === 0) return;
    const prev = [...pageHistory];
    prev.pop();
    const prevKey = prev.length > 0 ? prev[prev.length - 1] : undefined;
    setPageHistory(prev);
    fetchPage(prevKey);
  };

  const handleFirstPage = () => {
    setPageHistory([]);
    fetchPage();
  };

  const handleSyncSelling = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await api.syncSellingStatus();
      setSyncResult({ type: 'success', message: `Sync complete: ${result.total_synced} products synced, ${result.newly_selling} newly selling, ${result.newly_not_selling} newly not selling` });
      setTimeout(() => setSyncResult(null), 5000);
      handleFirstPage();
    } catch (err) {
      console.error('Sync failed:', err);
      setSyncResult({ type: 'error', message: 'Selling status sync failed' });
      setTimeout(() => setSyncResult(null), 5000);
    } finally {
      setSyncing(false);
    }
  };

  const currentPage = pageHistory.length + 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Filter bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', flexShrink: 0, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Filters:</span>
        <input
          aria-label="Filter by category"
          placeholder="Category..."
          value={filters.category}
          onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
          style={{ padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, width: 150 }}
        />
        <input
          aria-label="Filter by brand"
          placeholder="Brand..."
          value={filters.brand}
          onChange={(e) => setFilters((f) => ({ ...f, brand: e.target.value }))}
          style={{ padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, width: 150 }}
        />
        <select
          aria-label="Filter by selling status"
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          style={{ padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
        >
          <option value="">All Status</option>
          <option value="selling">Selling</option>
          <option value="not_selling">Not Selling</option>
        </select>
        <button onClick={() => { setFilters({ category: '', brand: '', status: '' }); }}
          style={{ padding: '5px 10px', background: 'white', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}>
          Clear
        </button>
        <div style={{ flex: 1 }} />
        <button onClick={handleSyncSelling} disabled={syncing}
          style={{ padding: '5px 12px', background: '#059669', color: 'white', border: 'none', borderRadius: 4, fontSize: 13, cursor: syncing ? 'not-allowed' : 'pointer', opacity: syncing ? 0.6 : 1 }}>
          {syncing ? 'Syncing...' : 'Sync Selling Status'}
        </button>
      </div>

      {/* Sync result banner */}
      {syncResult && (
        <div style={{
          padding: '8px 16px',
          background: syncResult.type === 'success' ? '#f0fdf4' : '#fef2f2',
          color: syncResult.type === 'success' ? '#15803d' : '#dc2626',
          borderBottom: `1px solid ${syncResult.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          fontSize: 13,
        }}>
          {syncResult.message}
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div style={{ padding: '8px 16px', background: '#fef2f2', color: '#dc2626', borderBottom: '1px solid #fecaca', fontSize: 13 }}>
          {error}
          <button onClick={() => fetchPage()} style={{ marginLeft: 12, padding: '2px 8px', background: 'white', border: '1px solid #fecaca', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && products.length === 0 && (
        <div style={{ padding: '12px 16px', background: '#f0f9ff', color: '#0369a1', borderBottom: '1px solid #bae6fd', fontSize: 13 }}>
          No products found. The database may be empty or filters too restrictive.
        </div>
      )}

      {/* Bulk edit bar */}
      <BulkEditBar
        selectedCprcodes={selectedCprcodes}
        onClearSelection={() => setSelectedCprcodes([])}
        onBulkEditComplete={() => fetchPage()}
      />

      {/* Grid */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <ProductGrid
          rowData={products}
          loading={loading}
          onProductClick={setDetailCprcode}
          onSelectionChanged={setSelectedCprcodes}
        />
      </div>

      {/* Server-side pagination bar */}
      <div data-testid="pagination-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', borderTop: '1px solid #e5e7eb', background: '#f9fafb', flexShrink: 0 }}>
        <span data-testid="page-info" style={{ fontSize: 13, color: '#6b7280' }}>
          Page {currentPage} &middot; {products.length} items
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            data-testid="first-page"
            onClick={handleFirstPage}
            disabled={currentPage === 1 || loading}
            style={{ padding: '4px 10px', background: 'white', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, cursor: 'pointer', opacity: currentPage === 1 || loading ? 0.4 : 1 }}
          >
            First
          </button>
          <button
            data-testid="prev-page"
            onClick={handlePrevPage}
            disabled={currentPage === 1 || loading}
            style={{ padding: '4px 10px', background: 'white', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, cursor: 'pointer', opacity: currentPage === 1 || loading ? 0.4 : 1 }}
          >
            Prev
          </button>
          <button
            data-testid="next-page"
            onClick={handleNextPage}
            disabled={!lastKey || loading}
            style={{ padding: '4px 10px', background: 'white', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, cursor: 'pointer', opacity: !lastKey || loading ? 0.4 : 1 }}
          >
            Next
          </button>
        </div>
      </div>

      {/* Detail panel */}
      {detailCprcode && (
        <ProductDetail
          cprcode={detailCprcode}
          onClose={() => setDetailCprcode(null)}
          onSaved={() => fetchPage()}
        />
      )}
    </div>
  );
}
